import { Button, Input, Label, Select, Spinner } from '@crm/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { Facebook, Instagram, Mail, MessageCircle } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { FormError, FormField, FormSuccess, XIcon } from '@/components';
import { useUpdateProfile } from '@/modules/auth/hooks';
import { type ProfileFormValues, profileSchema } from '@/modules/auth/schemas';
import type { PublicUser } from '@/modules/auth/types';
import { toErrorMessage } from '@/modules/auth/utils';

const GENEROS = [
  { value: '', label: 'Prefiro nao informar' },
  { value: 'FEMALE', label: 'Feminino' },
  { value: 'MALE', label: 'Masculino' },
] as const;

/** O X nao tem icone proprio no lucide - a letra e a marca. */
/**
 * Dados pessoais do usuario logado. A foto nao esta aqui: ela e trocada na
 * propria faixa do topo, onde ja aparece - repetida nos dois lugares, ficava
 * a duvida de qual das duas valia.
 */
export function ProfileForm({ user }: { user: PublicUser }) {
  const updateProfile = useUpdateProfile();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ProfileFormValues>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      name: user.name,
      image: user.image ?? '',
      phone: user.phone ?? '',
      gender: user.gender ?? '',
      instagram: user.instagram ?? '',
      facebook: user.facebook ?? '',
      x: user.x ?? '',
    },
  });

  return (
    <form
      className="space-y-4"
      onSubmit={handleSubmit((values) =>
        updateProfile.mutate({
          name: values.name,
          phone: values.phone,
          gender: values.gender === '' ? null : values.gender,
          instagram: values.instagram,
          facebook: values.facebook,
          x: values.x,
        }),
      )}
      noValidate
    >
      <FormError message={updateProfile.error ? toErrorMessage(updateProfile.error) : null} />

      {updateProfile.isSuccess ? <FormSuccess message="Perfil atualizado." /> : null}

      <div className="grid gap-4 sm:grid-cols-2">
        <FormField
          label="Nome completo"
          autoComplete="name"
          error={errors.name?.message}
          {...register('name')}
        />

        {/* O e-mail e a chave de entrada: trocar aqui derrubaria o login, e
            isso e assunto do administrador. */}
        <div className="space-y-2">
          <Label htmlFor="email">E-mail</Label>
          <div className="relative">
            <Mail
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden
            />
            <Input id="email" value={user.email} readOnly disabled className="pl-9" />
          </div>
          <p className="text-xs text-muted-foreground">E o e-mail usado para entrar.</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="phone">WhatsApp</Label>
          <div className="relative">
            <MessageCircle
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden
            />
            <Input
              id="phone"
              placeholder="(11) 90000-0000"
              autoComplete="tel"
              className="pl-9"
              {...register('phone')}
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="gender">Genero</Label>
          <Select id="gender" {...register('gender')}>
            {GENEROS.map((genero) => (
              <option key={genero.value} value={genero.value}>
                {genero.label}
              </option>
            ))}
          </Select>
        </div>
      </div>

      <div className="space-y-3 border-t border-border pt-4">
        <p className="text-sm font-medium">Redes sociais</p>

        <div className="grid gap-4 sm:grid-cols-3">
          <div className="space-y-2">
            <Label htmlFor="instagram">Instagram</Label>
            <div className="relative">
              <Instagram
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden
              />
              <Input
                id="instagram"
                placeholder="@seuperfil"
                className="pl-9"
                {...register('instagram')}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="facebook">Facebook</Label>
            <div className="relative">
              <Facebook
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden
              />
              <Input
                id="facebook"
                placeholder="/seuperfil"
                className="pl-9"
                {...register('facebook')}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="x">X</Label>
            <div className="relative">
              <XIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input id="x" placeholder="@seuperfil" className="pl-9" {...register('x')} />
            </div>
          </div>
        </div>
      </div>

      <Button type="submit" disabled={updateProfile.isPending}>
        {updateProfile.isPending ? <Spinner /> : null}
        Salvar alteracoes
      </Button>
    </form>
  );
}
